import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { callAdmin } from '@/integrations/supabase/client';

interface SubscriptionPlan {
  id: string;
  plan_key: string;
  name: string;
  description: string;
  price_monthly: number;
  price_six_monthly: number;
  price_yearly: number;
  stripe_price_monthly_id: string;
  stripe_price_six_monthly_id: string;
  stripe_price_yearly_id: string;
  features: string[];
  max_conversations: number;
  max_categories: number;
  priority_support: boolean;
  is_active: boolean;
  is_subscription: boolean;
  voice_tokens_per_month?: number;
  voice_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

export const SubscriptionManagement: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [statusTab, setStatusTab] = useState<'active' | 'inactive' | 'all'>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    plan_key: '',
    name: '',
    description: '',
    price_monthly: 0,
    price_six_monthly: 0,
    price_yearly: 0,
    stripe_price_monthly_id: '',
    stripe_price_six_monthly_id: '',
    stripe_price_yearly_id: '',
    features: [] as string[],
    max_conversations: -1,
    max_categories: -1,
    priority_support: false,
    is_active: true,
    is_subscription: true,
    voice_tokens_per_month: 0,
    voice_enabled: false
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const result = await callAdmin('get_subscription_plans')
      if (result.success) setPlans(result.data as SubscriptionPlan[])
      else setError(result.error || 'Failed to fetch subscription plans')
    } catch (err) {
      setError('Failed to fetch subscription plans');
      console.error('Error fetching subscription plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setEditForm({
      plan_key: plan.plan_key || '',
      name: plan.name,
      description: plan.description,
      price_monthly: plan.price_monthly,
      price_six_monthly: plan.price_six_monthly || 0,
      price_yearly: plan.price_yearly,
      stripe_price_monthly_id: plan.stripe_price_monthly_id || '',
      stripe_price_six_monthly_id: plan.stripe_price_six_monthly_id || '',
      stripe_price_yearly_id: plan.stripe_price_yearly_id || '',
      features: plan.features,
      max_conversations: plan.max_conversations,
      max_categories: plan.max_categories,
      priority_support: plan.priority_support,
      is_active: plan.is_active,
      is_subscription: plan.is_subscription !== false,
      voice_tokens_per_month: plan.voice_tokens_per_month || 0,
      voice_enabled: plan.voice_enabled || false
    });
    setShowEditModal(true);
  };

  const handleCreatePlan = () => {
    setEditForm({
      plan_key: '',
      name: '',
      description: '',
      price_monthly: 0,
      price_six_monthly: 0,
      price_yearly: 0,
      stripe_price_monthly_id: '',
      stripe_price_six_monthly_id: '',
      stripe_price_yearly_id: '',
      features: [],
      max_conversations: -1,
      max_categories: -1,
      priority_support: false,
      is_active: true,
      is_subscription: true,
      voice_tokens_per_month: 50000,
      voice_enabled: true
    });
    setShowCreateModal(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlan) return;

    try {
      const result = await callAdmin('update_subscription_plan', { id: selectedPlan.id, ...editForm })

      if (result.success) {
        setShowEditModal(false);
        fetchPlans();
      } else {
        setError(result.error || 'Failed to update plan');
      }
    } catch (err) {
      setError('Failed to update plan');
      console.error('Error updating plan:', err);
    }
  };

  const handleCreateNewPlan = async () => {
    try {
      const result = await callAdmin('create_subscription_plan', editForm)

      if (result.success) {
        setShowCreateModal(false);
        fetchPlans();
      } else {
        setError(result.error || 'Failed to create plan');
      }
    } catch (err) {
      setError('Failed to create plan');
      console.error('Error creating plan:', err);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this subscription plan?')) return;

    try {
      const result = await callAdmin('delete_subscription_plan', { id: planId })

      if (result.success) {
        fetchPlans();
      } else {
        setError(result.error || 'Failed to delete plan');
      }
    } catch (err) {
      setError('Failed to delete plan');
      console.error('Error deleting plan:', err);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setEditForm({
        ...editForm,
        features: [...editForm.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setEditForm({
      ...editForm,
      features: editForm.features.filter((_, i) => i !== index)
    });
  };

  const getPlanColor = (plan: SubscriptionPlan) => {
    if (plan.name.toLowerCase().includes('free')) return 'bg-slate-900/60 border-slate-700/60 hover:border-slate-500';
    if (plan.name.toLowerCase().includes('premium')) return 'bg-blue-950/30 border-blue-500/30 hover:border-blue-500/60';
    if (plan.name.toLowerCase().includes('family')) return 'bg-purple-950/30 border-purple-500/30 hover:border-purple-500/60';
    return 'bg-emerald-950/30 border-emerald-500/30 hover:border-emerald-500/60';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const activePlans = plans.filter(p => p.is_active);
  const inactivePlans = plans.filter(p => !p.is_active);
  const displayedPlans = statusTab === 'active' ? activePlans : statusTab === 'inactive' ? inactivePlans : plans;

  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Subscription Plan Management</h2>
          <p className="text-muted-foreground">Manage subscription plans and pricing</p>
        </div>
        <Button onClick={handleCreatePlan} className="px-6 bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
          Add New Plan
        </Button>
      </div>

      {/* Active / Inactive Tabs */}
      <div className="mb-6 flex items-center gap-2">
        {([
          { id: 'active', label: '✅ Active', count: activePlans.length, activeColor: 'bg-emerald-600 text-white' },
          { id: 'inactive', label: '⛔ Inactive', count: inactivePlans.length, activeColor: 'bg-red-600 text-white' },
          { id: 'all', label: '🗂 All Plans', count: plans.length, activeColor: 'bg-slate-600 text-white' },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onClick={() => setStatusTab(tab.id)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
              statusTab === tab.id
                ? `${tab.activeColor} border-transparent shadow-md`
                : 'bg-slate-900/60 text-muted-foreground border-border hover:text-foreground hover:bg-slate-800'
            }`}
          >
            {tab.label} <span className="ml-1 opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-red-400">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-400">Error</h3>
              <div className="mt-2 text-sm text-red-300">{error}</div>
            </div>
          </div>
        </div>
      )}

      {displayedPlans.length === 0 && (
        <div className="text-center py-16 bg-slate-900/40 rounded-2xl border border-dashed border-border">
          <p className="text-muted-foreground font-medium">
            {statusTab === 'inactive' ? 'No inactive plans.' : 'No plans found.'}
          </p>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayedPlans.map((plan) => (
          <Card key={plan.id} className={`p-6 border-2 transition-all ${getPlanColor(plan)}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-foreground mb-2">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>

                <div className="mb-4">
                  <div className="text-3xl font-bold text-foreground">
                    ${plan.price_monthly}
                    <span className="text-lg font-normal text-muted-foreground">/month</span>
                  </div>
                  {plan.price_six_monthly > 0 && (
                    <div className="text-sm text-muted-foreground">
                      ${plan.price_six_monthly}/6 months (${(plan.price_six_monthly / 6).toFixed(2)}/month)
                    </div>
                  )}
                  {plan.price_yearly > 0 && (
                    <div className="text-sm text-muted-foreground">
                      ${plan.price_yearly}/year (${(plan.price_yearly / 12).toFixed(2)}/month)
                    </div>
                  )}
                  <div className="text-xs font-medium text-emerald-400 mt-1">
                    {plan.is_subscription ? 'Recurring Subscription' : 'One-time Payment'}
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-foreground">Conversations:</span>
                    <span className="ml-2 text-muted-foreground">
                      {plan.max_conversations === -1 ? 'Unlimited' : plan.max_conversations}
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-foreground">Categories:</span>
                    <span className="ml-2 text-muted-foreground">
                      {plan.max_categories === -1 ? 'Unlimited' : plan.max_categories}
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-foreground">Voice Tokens/Month:</span>
                    <span className="ml-2 text-muted-foreground">
                      🎤 {plan.voice_tokens_per_month ? plan.voice_tokens_per_month.toLocaleString() : '0'}
                    </span>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-foreground">Priority Support:</span>
                    <span className="ml-2 text-muted-foreground">
                      {plan.priority_support ? '✅' : '❌'}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-medium text-foreground mb-2">Features:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-emerald-400 mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${plan.is_active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditPlan(plan)}
                      className="text-sky-400 hover:text-sky-300 text-sm font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan.id)}
                      className="text-red-400 hover:text-red-300 text-sm font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Edit/Create Plan Modal */}
      {(showEditModal || showCreateModal) && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden">
          <div className="relative border border-border w-full max-w-2xl max-h-[88vh] shadow-2xl rounded-2xl bg-card text-foreground flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
              <h3 className="text-xl font-bold text-foreground">
                {showCreateModal ? 'Create New Plan' : 'Edit Plan'}
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowCreateModal(false);
                }}
                className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-secondary text-sm font-bold"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Plan Key * <span className="text-xs text-muted-foreground">(used in Stripe)</span>
                  </label>
                  <Input
                    type="text"
                    value={editForm.plan_key || ''}
                    onChange={(e) => setEditForm({ ...editForm, plan_key: e.target.value })}
                    className="w-full"
                    placeholder="e.g., premium_monthly"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Plan Name *
                  </label>
                  <Input
                    type="text"
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full"
                    placeholder="e.g., Premium"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Monthly Price ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price_monthly || 0}
                    onChange={(e) => setEditForm({ ...editForm, price_monthly: parseFloat(e.target.value) || 0 })}
                    className="w-full"
                    placeholder="9.99"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    6-Month Price ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price_six_monthly || 0}
                    onChange={(e) => setEditForm({ ...editForm, price_six_monthly: parseFloat(e.target.value) || 0 })}
                    className="w-full"
                    placeholder="54.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Yearly Price ($)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price_yearly || 0}
                    onChange={(e) => setEditForm({ ...editForm, price_yearly: parseFloat(e.target.value) || 0 })}
                    className="w-full"
                    placeholder="99.99"
                  />
                </div>

                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    id="is_subscription"
                    checked={editForm.is_subscription}
                    onChange={(e) => setEditForm({ ...editForm, is_subscription: e.target.checked })}
                    className="h-4 w-4 text-emerald-500 focus:ring-emerald-400 border-border rounded"
                  />
                  <label htmlFor="is_subscription" className="ml-2 block text-sm font-medium text-foreground">
                    Is Recurring Subscription?
                  </label>
                </div>

                {(editForm?.stripe_price_monthly_id || editForm?.stripe_price_six_monthly_id || editForm?.stripe_price_yearly_id) && (
                  <div className="md:col-span-2 p-4 bg-sky-950/30 border border-sky-500/30 rounded-xl">
                    <h4 className="text-sm font-bold text-sky-400 mb-2">Stripe Integration</h4>
                    {editForm?.stripe_price_monthly_id && (
                      <p className="text-xs text-sky-300 mb-1">
                        Monthly Price ID: <code className="bg-sky-900/50 px-1 py-0.5 rounded text-sky-200">{editForm.stripe_price_monthly_id}</code>
                      </p>
                    )}
                    {editForm?.stripe_price_six_monthly_id && (
                      <p className="text-xs text-sky-300 mb-1">
                        6-Month Price ID: <code className="bg-sky-900/50 px-1 py-0.5 rounded text-sky-200">{editForm.stripe_price_six_monthly_id}</code>
                      </p>
                    )}
                    {editForm?.stripe_price_yearly_id && (
                      <p className="text-xs text-sky-300">
                        Yearly Price ID: <code className="bg-sky-900/50 px-1 py-0.5 rounded text-sky-200">{editForm.stripe_price_yearly_id}</code>
                      </p>
                    )}
                  </div>
                )}

                {showCreateModal && (
                  <div className="md:col-span-2 p-4 bg-emerald-950/30 border border-emerald-500/30 rounded-xl">
                    <p className="text-sm text-emerald-300">
                      ✨ Stripe products and prices will be automatically created when you save this plan
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Max Conversations
                  </label>
                  <Input
                    type="number"
                    value={editForm.max_conversations === -1 ? '' : editForm.max_conversations}
                    onChange={(e) => setEditForm({ ...editForm, max_conversations: e.target.value ? parseInt(e.target.value) : -1 })}
                    className="w-full"
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Max Categories
                  </label>
                  <Input
                    type="number"
                    value={editForm.max_categories === -1 ? '' : editForm.max_categories}
                    onChange={(e) => setEditForm({ ...editForm, max_categories: e.target.value ? parseInt(e.target.value) : -1 })}
                    className="w-full"
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="priority_support"
                    checked={editForm.priority_support}
                    onChange={(e) => setEditForm({ ...editForm, priority_support: e.target.checked })}
                    className="h-4 w-4 text-emerald-500 focus:ring-emerald-400 border-border rounded"
                  />
                  <label htmlFor="priority_support" className="ml-2 block text-sm text-foreground">
                    Priority Support
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="voice_enabled"
                    checked={editForm.voice_enabled}
                    onChange={(e) => setEditForm({ ...editForm, voice_enabled: e.target.checked })}
                    className="h-4 w-4 text-emerald-500 focus:ring-emerald-400 border-border rounded"
                  />
                  <label htmlFor="voice_enabled" className="ml-2 block text-sm text-foreground">
                    🎤 Voice Feature Enabled
                  </label>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Voice Tokens Per Month
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1000"
                    value={editForm.voice_tokens_per_month || 0}
                    onChange={(e) => setEditForm({ ...editForm, voice_tokens_per_month: parseInt(e.target.value) || 0 })}
                    className="w-full"
                    placeholder="50000"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Number of voice tokens included per month (e.g., 50000 for free, 500000 for premium)
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description || ''}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900/60 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    rows={3}
                    placeholder="Enter plan description"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground mb-1">
                    Features
                  </label>
                  <div className="space-y-2">
                    {editForm.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="flex-1 text-sm text-muted-foreground">• {feature}</span>
                        <button
                          onClick={() => removeFeature(index)}
                          className="text-red-400 hover:text-red-300 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <div className="flex space-x-2">
                      <Input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        className="flex-1"
                        placeholder="Add new feature"
                        onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                      />
                      <Button onClick={addFeature} className="px-4 bg-secondary hover:bg-secondary/80 text-foreground border border-border">
                        Add
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-emerald-500 focus:ring-emerald-400 border-border rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm font-medium text-foreground">
                    Active
                  </label>
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-border flex justify-end space-x-3 shrink-0 bg-card">
              <Button
                onClick={() => {
                  setShowEditModal(false);
                  setShowCreateModal(false);
                }}
                className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-foreground border border-border"
              >
                Cancel
              </Button>
              <Button
                onClick={showCreateModal ? handleCreateNewPlan : handleUpdatePlan}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg shadow-emerald-600/20"
              >
                {showCreateModal ? 'Create Plan' : 'Update Plan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
